// src/common/form_input/form_input.tsx

import React, { useState } from "react";
import { EyeIcon, EyeOffIcon } from "@heroicons/react/solid";

/**
 * Props interface for FormInput component
 */
interface FormInputProps {
  label: string;
  type: string;
  name: string;
  value: string;
  placeholder: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  touched?: boolean | undefined;
  error?: string | undefined;
  inputStyle?: string;
  className?: string; // Added className prop
  disabled?: boolean;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

/**
 * FormInput Component
 * Reusable form input component with password visibility toggle
 */
const FormInput: React.FC<FormInputProps> = ({
  label,
  type,
  name,
  value,
  placeholder,
  error,
  touched,
  onChange,
  onBlur,
  inputStyle = "",
  className = "", // Default empty string for className
  disabled = false,
  onFocus,
}) => {
  // State to control password visibility
  const [showPassword, setShowPassword] = useState(false);

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className={`mb-4 relative ${className}`}>
      <label className="block font-ubuntu text-sm text-primary" htmlFor={name}>
        {label}
      </label>
      <div className="relative">
        <input
          type={showPassword && type === "password" ? "text" : type}
          id={name}
          name={name}
          placeholder={placeholder}
          className={`w-full p-2 pr-10 border rounded-lg mt-1 font-ubuntu text-primary ${touched && error ? "border-red-500" : "border-gray-300"
            } ${inputStyle} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          disabled={disabled}
        />
        {/* Password visibility toggle button */}
        {type === "password" && (
          <button
            type="button"
            className="absolute inset-y-0 right-3 flex items-center focus:outline-none"
            onClick={togglePasswordVisibility}
          >
            {showPassword ? (
              <EyeOffIcon className="h-5 w-5 text-gray-500" />
            ) : (
              <EyeIcon className="h-5 w-5 text-gray-500" />
            )}
          </button>
        )}
      </div>
      {touched && error && <p className="text-red-500 text-sm">{error}</p>}
    </div>
  );
};

export default FormInput;